<template>
  <div class="card">
    <Toast />
    <div class="title">Daftar Mutasi Barang Per Supplier</div>

    <DataTable
      :value="initial_purchase_orders"
      :paginator="true"
      class="p-datatable-sm"
      :rows="limit"
      :totalRecords="total_records"
      :lazy="true"
      @page="getInitialPurchaseOrders($event)"
      data-key="id"
      :loading="loading.filter"
      :scrollable="true"
      scrollHeight="400px"
    >
      <template #header>
        <div class="p-grid p-fluid">
          <div class="p-col-12 p-md-3">
            <label for="mutation-code" class="block mb-1">Kode Mutasi</label>
            <InputText
              id="mutation-code"
              type="text"
              v-model="filter.mutation_code"
              placeholder="Cari kode mutasi..."
              @input="is_error.mutation_code = false"
              class="w-100"
            />
            <small class="p-error" v-if="is_error.mutation_code">{{
              error_msg.mutation_code
            }}</small>
          </div>

          <div class="p-col-12 p-md-3">
            <label for="doc-date" class="block mb-1">Tanggal Bon</label>
            <Calendar
              id="doc-date"
              v-model="filter.doc_date"
              :showIcon="true"
              dateFormat="dd/mm/yy"
              @date-select="is_error.doc_date = false"
              class="w-100"
            />
            <small class="p-error" v-if="is_error.doc_date">{{
              error_msg.doc_date
            }}</small>
          </div>

          <div class="p-col-12 p-md-3">
            <label for="supplier" class="block mb-1">Supplier</label>
            <span class="p-input-icon-right w-100" style="display: block">
              <i
                v-if="loading.suppliers"
                class="pi pi-spin pi-spinner"
                style="z-index: 10"
              ></i>
              <AutoComplete
                id="supplier"
                v-model="supplier_selected"
                :suggestions="autoFilteredValue"
                field="alias"
                @complete="searchSupplier($event)"
                :dropdown="true"
                placeholder="Cari supplier..."
                class="w-100"
              >
                <template #item="slotProps">
                  <div class="warehouse-item">
                    <span class="warehouse-text">{{
                      slotProps.item.alias
                    }}</span>
                  </div>
                </template>
              </AutoComplete>
            </span>
          </div>

          <div class="p-col-12 p-md-3">
            <label for="doc-number" class="block mb-1">Nomor Bon</label>
            <InputText
              id="doc-number"
              type="text"
              v-model="filter.doc_number"
              placeholder="Cari nomor bon..."
              @input="is_error.doc_number = false"
              class="w-100"
            />
            <small class="p-error" v-if="is_error.doc_number">{{
              error_msg.doc_number
            }}</small>
          </div>

          <div class="p-col-12 p-md-3 p-lg-3 p-d-flex p-ai-end">
            <Button
              label="Cari"
              icon="pi pi-search"
              @click="getInitialPurchaseOrders()"
              :loading="loading.filter"
              class="p-mr-2"
            />
            <Button
              label="Reset"
              icon="pi pi-refresh"
              @click="resetFilters()"
              class="p-button-secondary"
              v-tooltip.top="'Reset Filter'"
            />
          </div>
        </div>
      </template>

      <template #empty>
        <div class="empty-state">
          <i class="pi pi-inbox mb-3" style="font-size: 2rem"></i>
          <p>Data tidak tersedia</p>
        </div>
      </template>

      <template #loading>Sedang mengambil data. Mohon tunggu.</template>

      <Column field="no" header="No" :sortable="false">
        <template #body="slotProps">
          {{ (currentPage - 1) * rowsPerPage + slotProps.index + 1 }}
        </template>
      </Column>

      <Column field="mutation_code" header="Kode Mutasi">
        <template #body="slotProps">
          <span class="font-medium">{{ slotProps.data.mutation_code }}</span>
        </template>
      </Column>

      <Column field="supplier" header="Nama Supplier">
        <template #body="slotProps">
          <span class="text-truncate">
            <template
              v-if="
                slotProps.data.supplier_code && slotProps.data.supplier_name
              "
            >
              <span class="font-medium">{{
                slotProps.data.supplier_code
              }}</span>
              <span class="mx-2"> - </span>
              <span>{{ slotProps.data.supplier_name }}</span>
            </template>
            <span v-else>-</span>
          </span>
        </template>
      </Column>

      <Column field="total_item_type" header="Total Jenis Barang">
        <template #body="slotProps">
          {{ slotProps.data.total_item }}
        </template>
      </Column>

      <Column field="doc_date" header="Tanggal Bon">
        <template #body="slotProps">
          <span>
            {{ slotProps.data.doc_date }}
          </span>
        </template>
      </Column>
      <Column field="distribution_status" header="Status">
        <template #body="slotProps">
          <span
            :class="[
              'status-badge',
              `status-${slotProps.data.distribution_status}`
            ]"
          >
            {{ checkStatus(slotProps.data.distribution_status) }}
          </span>
        </template>
      </Column>
      <Column
        headerStyle="width: 4rem"
        bodyStyle="text-align: center; overflow: visible"
      >
        <template #body="slotProps">
          <div>
            <router-link
              :to="{
                path: '/purchase/detail',
                query: {
                  mutation_code: slotProps.data.mutation_code
                }
              }"
            >
              <Button
                type="button"
                icon="pi pi-eye"
                class="p-button-rounded p-mr-2"
              ></Button>
            </router-link>
            <Button
              type="button"
              icon="pi pi-trash"
              class="p-button-rounded p-button-warning"
              @click="confirmDelete(slotProps.data)"
              v-if="
                slotProps.data.status === 0 && user.role == 'OUTLET_MANAGER'
              "
            ></Button>
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog
      :visible.sync="delete_dialog"
      :style="{ width: '450px' }"
      header="Konfirmasi Hapus"
      :modal="true"
    >
      <div class="confirmation-content">
        Apakah kamu yakin ingin menghapus
        <b>{{ delete_data.doc_number }}</b
        >?
      </div>

      <template #footer>
        <Button
          label="Tidak"
          icon="pi pi-times"
          class="p-button-text"
          @click="delete_dialog = false"
        />
        <Button
          label="Ya"
          icon="pi pi-check"
          class="p-button-text"
          @click="deleteInitialPurchaseOrders()"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
const moment = require('moment')

export default {
  data() {
    return {
      moment: moment,
      loading: {
        filter: false,
        suppliers: false
      },
      filter: {
        doc_number: '',
        doc_date: '',
        mutation_code: '',
        status: ''
      },
      supplier_selected: null,
      autoFilteredValue: [],
      initial_purchase_orders: [],
      limit: 10,
      total_records: 0,
      currentPage: 1,
      rowsPerPage: 10,

      // Delete dialog
      delete_dialog: false,
      delete_data: {},

      // Error handling
      is_error: {
        mutation_code: false,
        doc_number: false,
        doc_date: false
      },
      error_msg: {
        mutation_code: '',
        doc_number: '',
        doc_date: ''
      }
    }
  },
  computed: mapState(['user', 'isAuthenticated']),
  mounted() {
    this.getInitialPurchaseOrders()
    // Initialize debounced search (don't load suppliers yet - lazy load on dropdown click)
    this.initDebouncedSearch()
  },
  beforeDestroy() {
    // Cleanup debounced function
    if (this.$_searchSuppliersDebounced) {
      this.$_searchSuppliersDebounced = null
    }
  },
  methods: {
    async getInitialPurchaseOrders(event) {
      // Filter for confirmed status only (status = 1)
      let status = '1'

      let sup_id = ''
      if (this.supplier_selected && this.supplier_selected.id) {
        sup_id = this.supplier_selected.id
      }

      let doc_date = this.filter.doc_date
        ? moment(this.filter.doc_date).format('YYYY-MM-DD')
        : ''

      let page = 1
      if (event) {
        page = event.page + 1
        this.currentPage = page
        this.rowsPerPage = event.rows
      }

      const offset = (page - 1) * this.limit

      // Build query parameters using URLSearchParams
      const params = new URLSearchParams({
        mutation_code: this.filter.mutation_code || '',
        sup_id: sup_id || '',
        doc_number: this.filter.doc_number || '',
        doc_date: doc_date || '',
        limit: this.limit,
        offset: offset,
        status: status,
        order_by: '-created_at'
      })

      this.loading.filter = true
      try {
        const response = await this.$http.get(
          `${
            process.env.VUE_APP_API_URL
          }/dashboard/initial-purchase-orders?${params.toString()}`
        )

        if (response.status === 200) {
          this.initial_purchase_orders =
            response.data.data.initial_purchase_orders
          this.total_records = response.data.data.total || 0
        }
      } catch (error) {
        console.error('Error fetching initial purchase orders:', error)
        this.showToast(
          'error',
          'Error',
          'Gagal mengambil data pembelian. Silakan coba lagi.'
        )
      } finally {
        this.loading.filter = false
      }
    },
    // Debounce utility method
    debounce(fn, delay) {
      let timeout
      return function (...args) {
        clearTimeout(timeout)
        timeout = setTimeout(() => fn.apply(this, args), delay)
      }
    },

    // API-based supplier loading with limit
    async getSuppliers(searchQuery = '', limit = 20) {
      this.loading.suppliers = true
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/suppliers`,
          {
            params: {
              search: searchQuery,
              limit: limit
            }
          }
        )

        if (response.status === 200) {
          return response.data.data.suppliers.map(item => {
            return {
              alias: `${item.code} - ${item.name}`,
              code: item.code,
              id: item.id,
              name: item.name,
              parent: item.parent
            }
          })
        }
        return []
      } catch (error) {
        console.error('Error fetching suppliers:', error)
        this.showToast('error', 'Error', 'Failed to fetch suppliers')
        return []
      } finally {
        this.loading.suppliers = false
      }
    },

    // Load initial suppliers for dropdown (first load when dropdown clicked)
    async loadSuppliersForDropdown() {
      const suppliers = await this.getSuppliers('', 20)
      this.autoFilteredValue = suppliers
    },

    // Actual search method that performs API call (called by debounced wrapper)
    async _searchSuppliers(event) {
      const query = event.query || ''

      // If empty query (dropdown clicked), always load initial suppliers
      // This ensures fresh data is always loaded when dropdown is opened
      if (!query) {
        await this.loadSuppliersForDropdown()
        return
      }

      // API call with search query
      const suppliers = await this.getSuppliers(query, 20)
      this.autoFilteredValue = suppliers
    },

    // Initialize debounced search function
    initDebouncedSearch() {
      if (!this.$_searchSuppliersDebounced) {
        this.$_searchSuppliersDebounced = this.debounce(
          this._searchSuppliers,
          500
        )
      }
    },

    // Wrapper that calls debounced version
    searchSupplier(event) {
      const query = event.query || ''

      // If empty query (dropdown clicked), execute immediately without debounce
      // This ensures dropdown opens quickly with fresh data
      if (!query) {
        this._searchSuppliers(event)
        return
      }

      // For typing/search, use debounce to reduce API calls
      if (!this.$_searchSuppliersDebounced) {
        this.initDebouncedSearch()
      }
      this.$_searchSuppliersDebounced(event)
    },
    // Reset all filters
    async resetFilters() {
      this.filter.doc_number = ''
      this.filter.doc_date = ''
      this.filter.mutation_code = ''
      this.filter.status = ''
      this.supplier_selected = null

      // Reload suppliers for fresh dropdown data
      await this.loadSuppliersForDropdown()
      this.getInitialPurchaseOrders()
    },

    checkStatus(status) {
      if (status === 0) {
        return 'Pending'
      } else if (status === 1) {
        return 'Partial'
      } else if (status === 2) {
        return 'Complete'
      } else {
        return 'Pending'
      }
    },

    confirmDelete(purchase) {
      this.delete_data = purchase
      this.delete_dialog = true
    },

    async deleteInitialPurchaseOrders() {
      try {
        const response = await this.$http.delete(
          `${process.env.VUE_APP_API_URL}/dashboard/initial-purchase-orders/${this.delete_data.id}`
        )

        this.delete_dialog = false

        if (response.status === 200) {
          this.showToast('success', 'Berhasil hapus pembelian')
          this.getInitialPurchaseOrders()
        } else {
          const errorMessage =
            (response.data && response.data.message) ||
            'Gagal menghapus pembelian'
          this.showToast('error', 'Gagal hapus pembelian', errorMessage)
        }
      } catch (error) {
        const errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          'Terjadi kesalahan saat menghapus pembelian'
        this.showToast('error', 'Gagal hapus pembelian', errorMessage)
        console.error('Error deleting initial purchase order:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.card {
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14),
    0 1px 3px 0 rgba(0, 0, 0, 0.12);
  padding: 1.5rem;

  .title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 1.5rem;
  }
}

.warehouse-item {
  padding: 0.5rem;

  .warehouse-text {
    font-size: 0.875rem;
    color: #495057;
  }
}

.w-100 {
  width: 100%;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #6c757d;

  i {
    color: #2196f3;
  }
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-block;
}

.text-right {
  text-align: right;
}

.font-medium {
  font-weight: 500;
}

.mx-2 {
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  min-width: 120px;
  white-space: nowrap;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
  min-width: 6rem;

  &.status-0 {
    background-color: #fff3e0;
    color: #e65100;
    border: 1px solid #ffcc80;
  }

  &.status-1 {
    background-color: #feedaf;
    color: #8a5340;
    border: 1px solid #fdd835;
  }

  &.status-2 {
    background-color: #e8f5e9;
    color: #2e7d32;
    border: 1px solid #a5d6a7;
  }
}

.w-100 {
  width: 100%;
}

:deep(.p-datatable) {
  position: relative;
  width: 100%;
  overflow-x: auto;

  .p-datatable-thead > tr > th {
    background-color: #f8f9fa;
    padding: 0.75rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #495057;
    position: sticky;
    top: 0;
    z-index: 0;
    text-align: center;
    border-bottom: 2px solid #dee2e6;
    white-space: nowrap;

    &:nth-child(1) {
      width: 2.5rem;
      min-width: 2.5rem;
    } /* No */
    &:nth-child(2) {
      width: 8rem;
      min-width: 8rem;
    } /* Kode Mutasi */
    &:nth-child(3) {
      width: 15rem;
      min-width: 15rem;
    } /* Nama Supplier */
    &:nth-child(4) {
      width: 8rem;
      min-width: 8rem;
    } /* Total Jenis Barang */
    &:nth-child(5) {
      width: 7rem;
      min-width: 7rem;
    } /* Tanggal Mutasi */
    &:nth-child(6) {
      width: 5rem;
      min-width: 5rem;
    } /* Status */
    &:nth-child(7) {
      width: 5rem;
      min-width: 5rem;
    } /* Aksi */
  }

  .p-datatable-tbody > tr > td {
    padding: 0.75rem;
    font-size: 0.875rem;
    background: #ffffff;
    border-bottom: 1px solid #dee2e6;
    white-space: nowrap;

    &:nth-child(1),
    &:nth-child(4),
    &:nth-child(5) {
      text-align: center;
    }

    &:nth-child(2),
    &:nth-child(3) {
      text-align: left;
    }

    &:nth-child(6) {
      text-align: center;
    }

    &:nth-child(7) {
      text-align: center;
    }

    &:nth-child(1) {
      width: 2.5rem;
      min-width: 2.5rem;
    } /* No */
    &:nth-child(2) {
      width: 8rem;
      min-width: 8rem;
    } /* Kode Mutasi */
    &:nth-child(3) {
      width: 15rem;
      min-width: 15rem;
    } /* Nama Supplier */
    &:nth-child(4) {
      width: 8rem;
      min-width: 8rem;
    } /* Total Jenis Barang */
    &:nth-child(5) {
      width: 7rem;
      min-width: 7rem;
    } /* Tanggal Mutasi */
    &:nth-child(6) {
      width: 5rem;
      min-width: 5rem;
    } /* Status */
    &:nth-child(7) {
      width: 5rem;
      min-width: 5rem;
    } /* Aksi */
  }
}
</style>
